import path from 'path';
import fs from 'fs';
import { Logger } from '@unidev-hub/logger';

/**
 * Resolves paths intelligently with fallbacks and environment support
 */
export class PathResolver {
  private projectRoot: string;
  private logger: Logger;

  constructor(logger: Logger) {
    this.projectRoot = process.cwd();
    this.logger = logger;
  }

  /**
   * Resolve config file path with automatic environment detection
   *
   * @param customPath Optional explicit path to config file
   * @param filename Default filename to use
   * @returns Resolved config file path
   */
  resolveConfigPath(customPath?: string, filename = 'config.yaml'): string {
    if (customPath) {
      // If absolute path provided, use it directly
      if (path.isAbsolute(customPath)) {
        return customPath;
      }
      // Otherwise treat as relative to project root
      return path.join(this.projectRoot, customPath);
    }

    const environment = process.env.NODE_ENV || 'development';

    // Try environment-specific config first
    const envConfigPath = path.join(this.projectRoot, 'config', `config.${environment}.yaml`);
    if (fs.existsSync(envConfigPath)) {
      this.logger.debug(`Using environment-specific config: ${envConfigPath}`);
      return envConfigPath;
    }

    // Fallback to checking if environment-specific config exists with .yml extension
    const envConfigPathYml = path.join(this.projectRoot, 'config', `config.${environment}.yml`);
    if (fs.existsSync(envConfigPathYml)) {
      this.logger.debug(`Using environment-specific config: ${envConfigPathYml}`);
      return envConfigPathYml;
    }

    // Then try default config in config folder
    const defaultConfigPath = path.join(this.projectRoot, 'config', filename);
    if (fs.existsSync(defaultConfigPath)) {
      this.logger.debug(`Using default config: ${defaultConfigPath}`);
      return defaultConfigPath;
    }

    // Try with .yml extension
    const defaultConfigPathYml = path.join(this.projectRoot, 'config', filename.replace('.yaml', '.yml'));
    if (fs.existsSync(defaultConfigPathYml)) {
      this.logger.debug(`Using default config: ${defaultConfigPathYml}`);
      return defaultConfigPathYml;
    }

    // Last resort: look in the root folder
    const rootConfigPath = path.join(this.projectRoot, filename);
    this.logger.debug(`Falling back to root config: ${rootConfigPath}`);
    return rootConfigPath;
  }

  /**
   * Resolve a path relative to the project root
   *
   * @param relativePath Path relative to project root
   * @returns Absolute path
   */
  resolveFromRoot(relativePath: string): string {
    return path.join(this.projectRoot, relativePath);
  }

  /**
   * Check if a file or directory exists
   *
   * @param filePath Path to check
   * @returns true if exists, false otherwise
   */
  exists(filePath: string): boolean {
    return fs.existsSync(filePath);
  }

  /**
   * Find the first existing file from a list of possible paths
   *
   * @param paths List of paths to check
   * @returns First existing path or undefined if none exist
   */
  findFirstExisting(paths: string[]): string | undefined {
    for (const p of paths) {
      if (this.exists(p)) {
        return p;
      }
    }
    return undefined;
  }
}

/**
 * Create a new PathResolver instance
 */
export function createPathResolver(logger: Logger): PathResolver {
  return new PathResolver(logger);
}
